import { Controller } from "@hotwired/stimulus";
import "./component.scss";

const POLL_INTERVAL_MS = 30000;
const POLL_FAILURE_BACKOFF_MS = 2 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 3;
const UNREAD_COUNT_UPDATED_EVENT = "notifications:unread-count-updated";

let pollingOwner = null;
let pollingIntervalId = null;
let unreadCountRequest = null;
let consecutivePollFailures = 0;
let pollBackoffUntil = null;
const activeControllers = new Set();

const normalizeCount = (count) => Number(count) || 0;

// The event carries every badge's number, not just the bell's: the chat badge
// rides along on this poll so it stays live without a Matrix client (BRY-928).
// chatCount stays undefined when a caller only knows the bell's number, so
// listeners can tell "no chat news" from "nothing said about chat".
const broadcastUnreadCount = (count, chatCount) => {
  document.dispatchEvent(
    new CustomEvent(UNREAD_COUNT_UPDATED_EVENT, {
      detail: {
        count: normalizeCount(count),
        chatCount: chatCount === undefined ? undefined : normalizeCount(chatCount),
      },
    })
  );
};

const fetchUnreadCount = async () => {
  if (unreadCountRequest) {
    return unreadCountRequest;
  }

  unreadCountRequest = (async () => {
    const response = await fetch("/notifications/unread_count", {
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      const error = new Error(`Unread count request failed with status ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      const error = new Error("Unread count response is not JSON");
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return { count: normalizeCount(data.count), chatCount: normalizeCount(data.chat_count) };
  })();

  try {
    return await unreadCountRequest;
  } finally {
    unreadCountRequest = null;
  }
};

const startPolling = () => {
  if (pollingIntervalId) {
    return;
  }

  pollingIntervalId = window.setInterval(() => {
    if (!pollingOwner || shouldPausePolling()) {
      return;
    }
    pollingOwner.pollUnreadCount();
  }, POLL_INTERVAL_MS);
};

const stopPolling = () => {
  if (!pollingIntervalId) {
    return;
  }
  clearInterval(pollingIntervalId);
  pollingIntervalId = null;
};

const resetPollFailureState = () => {
  consecutivePollFailures = 0;
  pollBackoffUntil = null;
};

const shouldPausePolling = () => pollBackoffUntil && Date.now() < pollBackoffUntil;

const registerPollFailure = () => {
  consecutivePollFailures += 1;

  if (consecutivePollFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
    pollBackoffUntil = Date.now() + POLL_FAILURE_BACKOFF_MS;
  }
};

export default class extends Controller {
  static targets = ["badge", "button", "dropdown"];

  connect() {
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    this.boundHandleTurboStream = this.handleTurboStream.bind(this);
    this.boundHandleMarkAllAsRead = this.handleMarkAllAsRead.bind(this);
    this.boundHandleFrameRender = this.handleFrameRender.bind(this);
    this.boundHandleUnreadCountUpdated = this.handleUnreadCountUpdated.bind(this);

    document.addEventListener("turbo:before-stream-render", this.boundHandleTurboStream);
    document.addEventListener("turbo:submit-end", this.boundHandleMarkAllAsRead);
    document.addEventListener("turbo:frame-render", this.boundHandleFrameRender);
    document.addEventListener(UNREAD_COUNT_UPDATED_EVENT, this.boundHandleUnreadCountUpdated);

    activeControllers.add(this);
    this.electPollingOwner();
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleOutsideClick);
    document.removeEventListener("turbo:before-stream-render", this.boundHandleTurboStream);
    document.removeEventListener("turbo:submit-end", this.boundHandleMarkAllAsRead);
    document.removeEventListener("turbo:frame-render", this.boundHandleFrameRender);
    document.removeEventListener(UNREAD_COUNT_UPDATED_EVENT, this.boundHandleUnreadCountUpdated);

    activeControllers.delete(this);
    if (this.isPollingOwner()) {
      pollingOwner = null;
    }
    this.electPollingOwner();
  }

  toggle(event) {
    event.stopPropagation();

    // On mobile, redirect to notifications page instead of showing dropdown
    if (this.isMobile()) {
      window.location.href = "/notifications";
      return;
    }

    const isExpanded = this.buttonTarget.getAttribute("aria-expanded") === "true";

    if (isExpanded) {
      this.close();
    } else {
      this.open();
    }
  }

  isMobile() {
    // Check if viewport width is mobile (< 768px, matching typical Tailwind md breakpoint)
    return window.innerWidth < 768;
  }

  open() {
    this.dropdownTarget.classList.remove("hidden");
    this.buttonTarget.setAttribute("aria-expanded", "true");
    document.addEventListener("click", this.boundHandleOutsideClick);
    document.body.style.overflow = "hidden";
  }

  close() {
    this.dropdownTarget.classList.add("hidden");
    this.buttonTarget.setAttribute("aria-expanded", "false");
    document.removeEventListener("click", this.boundHandleOutsideClick);
    document.body.style.overflow = "";
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  handleTurboStream(event) {
    if (!this.isPollingOwner()) {
      return;
    }

    const streamTarget = event.detail?.newStream?.target;
    if (typeof streamTarget === "string" && streamTarget.endsWith("notifications_dropdown")) {
      this.pollUnreadCount();
    }
  }

  handleFrameRender(event) {
    if (!this.isPollingOwner()) {
      return;
    }

    const frameId = this.dropdownTarget.querySelector("turbo-frame")?.id;
    if (frameId && event.target?.id === frameId) {
      this.pollUnreadCount();
    }
  }

  handleMarkAllAsRead(event) {
    if (!this.isPollingOwner()) {
      return;
    }

    const formElement = event.detail?.formSubmission?.formElement;
    const isMarkAllRead = formElement?.action?.includes("mark_all_as_read");

    if (isMarkAllRead && event.detail.success) {
      broadcastUnreadCount(0, 0);
    }
  }

  handleUnreadCountUpdated(event) {
    this.applyUnreadCount(event.detail?.count);
  }

  isPollingOwner() {
    return pollingOwner === this;
  }

  electPollingOwner() {
    const previousOwner = pollingOwner;

    if (!pollingOwner || !activeControllers.has(pollingOwner)) {
      pollingOwner = activeControllers.values().next().value || null;
    }

    if (pollingOwner) {
      startPolling();
      if (pollingOwner !== previousOwner) {
        pollingOwner.pollUnreadCount();
      }
    } else {
      stopPolling();
    }
  }

  async pollUnreadCount() {
    try {
      const { count, chatCount } = await fetchUnreadCount();
      resetPollFailureState();
      broadcastUnreadCount(count, chatCount);
    } catch (error) {
      if (error?.status === 401 || error?.status === 403) {
        stopPolling();
        return;
      }

      registerPollFailure();
      console.error("Failed to update notification badge:", error);
    }
  }

  applyUnreadCount(count) {
    if (!this.hasBadgeTarget) {
      return;
    }

    const normalizedCount = normalizeCount(count);
    if (normalizedCount > 0) {
      const displayCount = normalizedCount > 99 ? "99+" : normalizedCount.toString();
      this.badgeTarget.textContent = displayCount;
      this.badgeTarget.style.display = "flex";
      return;
    }

    this.badgeTarget.style.display = "none";
    this.badgeTarget.textContent = "0";
  }
}
